import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Minus, Plus, ShieldCheck, Truck, RotateCcw, Heart } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl } from "@/lib/api";
import { track } from "@/lib/analytics";
import { useCart } from "@/context/CartContext";
import Reveal from "@/components/Reveal";
import SizeChart from "@/components/SizeChart";
import ReviewsSection from "@/components/ReviewsSection";
import useCharity from "@/lib/useCharity";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import useSEO from "@/lib/useSEO";

const COLOR_SWATCHES = {
  Black: "#111111", Natural: "#E8E2D4", Ash: "#B9B9B4", White: "#FAFAF7",
  "Heather Grey": "#B9B9B4", Grey: "#B9B9B4",
  Blossom: "#EFC9D4", Bay: "#B4C7D6", Butter: "#F8E08E", Seafoam: "#C4D7CE",
};
const LIGHT_COLORS = ["Natural", "White", "Blossom", "Bay", "Butter", "Seafoam", "White/Pink", "White/Black"];
const SIZE_ORDER = ["XS", "S", "M", "L", "XL", "XXL", "3XL", "4XL"];

// Sizes actually fulfillable per colorway: live Printify mapping + stock,
// intersected with the product's size list.
function sizesForColor(product, color) {
  const pf = (product.printify_sizes || {})[color];
  if (pf && pf.length) {
    const avail = (product.sizes && product.sizes.length ? product.sizes : SIZE_ORDER);
    return pf.filter((s) => avail.includes(s));
  }
  return product.sizes;
}

export default function ProductDetail() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [imgOk, setImgOk] = useState(true);
  const [size, setSize] = useState("M");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);
  const [imgStage, setImgStage] = useState(0);
  const [view, setView] = useState("front");
  const [closeupOk, setCloseupOk] = useState(true);
  const [related, setRelated] = useState([]);
  const charity = useCharity();

  useEffect(() => setImgStage(0), [color]);
  useEffect(() => setImgStage(0), [view]);

  // Approved on-body photos exist per colorway (owner-supplied set 2026-09-27).
  const activeColor = color || product?.color;
  const modelForColor = product
    ? (product.model_images?.[activeColor]
       || (product.model_image && (!product.model_image_color || product.model_image_color === activeColor)
           ? product.model_image : null))
    : null;
  const modelAvailable = Boolean(modelForColor);

  // Social preview + Product structured data follow the open product (incl. the
  // active colorway's on-body photo), so shared links match what the customer sees.
  const absImg = (() => {
    const m = modelForColor || product?.image || "";
    if (!m) return null;
    return m.startsWith("http") ? m : `${window.location.origin}${m}`;
  })();
  const productLd = product && !notFound ? {
    "@context": "https://schema.org", "@type": "Product",
    name: product.name, description: product.description,
    ...(absImg ? { image: [absImg] } : {}),
    brand: { "@type": "Brand", name: "Fleece The Regime" },
    offers: {
      "@type": "Offer", url: `https://www.fleecetheregime.com/product/${product.slug}/`,
      priceCurrency: "USD", price: product.price.toFixed(2), availability: "https://schema.org/InStock",
      hasMerchantReturnPolicy: { "@type": "MerchantReturnPolicy", applicableCountry: "US",
        returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow", merchantReturnDays: 30,
        returnMethod: "https://schema.org/ReturnByMail", refundType: "https://schema.org/FullRefund",
        itemDefectReturnFees: "https://schema.org/FreeReturn", merchantReturnLink: "https://www.fleecetheregime.com/faq/" },
    },
  } : null;

  useSEO(
    product ? `${product.name} — ${product.slogan} | Fleece The Regime` : notFound ? "Gone | Fleece The Regime" : "Fleece The Regime",
    product ? product.description : "Protest apparel with published specs, printed to order.",
    notFound,
    product && !notFound ? `/product/${slug}/` : null,
    { image: absImg, type: product ? "product" : null, jsonLd: productLd }
  );
  // Model-first: the approved on-body photo is the primary view whenever it exists
  // for the active color; with no photo the product image leads (never the drawn flat).
  useEffect(() => {
    if (modelAvailable) setView("model");
    else if (view === "model") setView("front");
  }, [activeColor, modelAvailable]);

  useEffect(() => {
    setImgOk(true);
    setCloseupOk(true);
    api
      .get(`/products/${slug}`)
      .then((r) => {
        setProduct(r.data);
        // Land on the approved model view when one exists: if the on-body photo is
        // color-locked, open the page on exactly that garment color.
        const d = r.data;
        // Same slogan in the other cuts (unisex tee / hoodie / women's tee), owner-approved
        // products only — the "complete the set" row under the purchase panel.
        api
          .get("/products")
          .then((all) => {
            const rank = { tee: 0, "womens-tee": 1, hoodie: 2, "baby-tee": 3 };
            setRelated(
              all.data
                .filter((x) => x.slug !== d.slug && x.slogan === d.slogan && !x.hidden)
                .sort((a, b) => (rank[a.type] ?? 9) - (rank[b.type] ?? 9))
                .slice(0, 3)
            );
          })
          .catch(() => {});
        // Card -> PDP color carry: ?color= wins, then the owner-approved card color
        const want = searchParams.get("color");
        const carried = want && d.colors?.includes(want) ? want : null;
        const initialColor = carried || d.card_color || (d.model_image && d.model_image_color ? d.model_image_color : d.color);
        setColor(initialColor);
        track("product_view", { slug: d.slug, color: initialColor });
        if (d.model_image || d.model_images) setView("model");
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <main className="pt-32 px-6 min-h-screen" data-testid="product-not-found">
        <p className="font-display uppercase text-4xl">This piece got deported.</p>
        <Link to="/shop" className="mt-6 inline-block font-mono text-sm underline">Back to the catalog</Link>
      </main>
    );
  }

  if (!product) {
    return <main className="pt-32 px-6 min-h-screen font-mono text-sm text-muted-foreground">Loading…</main>;
  }

  const posterBg = LIGHT_COLORS.includes(product.color) ? (product.color === "Natural" ? "bg-[#E8E2D4] text-black" : "bg-[#FAFAF7] text-black") : "bg-[#111] text-[#F4F4F0]";

  return (
    <main data-testid="product-detail-page" className="pt-16 min-h-screen">
      <div className="grid lg:grid-cols-2">
        <div className="relative border-b-2 lg:border-b-0 lg:border-r-2 border-black bg-black">
          <Link
            to="/shop"
            data-testid="product-back-link"
            className="absolute top-5 left-5 z-10 inline-flex items-center gap-2 border-2 border-black bg-background px-4 py-2 font-mono text-xs font-bold uppercase tracking-widest shadow-hard btn-press"
          >
            <ArrowLeft size={14} /> Catalog
          </Link>
          <div className="relative aspect-[4/5] lg:aspect-auto lg:h-[calc(100vh-4rem)] lg:sticky lg:top-16 overflow-hidden border-2 border-black shadow-hard">
            <div className={`absolute inset-0 flex items-center justify-center p-12 ${posterBg}`}>
              <span className="font-display uppercase text-center text-5xl sm:text-7xl leading-[0.95]">{product.slogan}</span>
            </div>
            {(() => {
              const activeColor = color || product.color;
              if (view === "model" && modelAvailable) {
                return (
                  <img
                    src={imgUrl(modelForColor)}
                    alt={`${product.name} — ${activeColor} on body`}
                    data-testid="model-image"
                    className="absolute inset-0 h-full w-full object-contain bg-[#F4F4F0]"
                  />
                );
              }
              const isBack = view === "back" && product.back_image;
              // No drawn-flat fallback: product photo leads, then nothing (slogan poster behind).
              const src = imgStage === 0
                  ? isBack
                    ? product.back_image
                    : product.image
                  : imgStage === 1 && isBack
                    ? product.image
                    : null;
              return src ? (
                <img
                  src={imgUrl(src)}
                  alt={`${product.name} — ${activeColor}${isBack ? " (back)" : ""}`}
                  onError={() => setImgStage((s) => s + 1)}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              ) : null;
            })()}
            {product.back_image && view === "front" && (
              <button
                data-testid="seen-on-back-flag"
                onClick={() => setView("back")}
                className="absolute top-5 right-5 z-10 border-2 border-black bg-primary px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-white shadow-hard btn-press"
              >
                Seen on the back
              </button>
            )}
            {(() => {
              // Owner rule (draft branch codex/shop-model-variety-and-copy): hide the flat
              // FRONT render whenever an approved on-body photo exists for this color.
              const views = [...(modelAvailable ? ["model"] : ["front"]), ...(product.back_image ? ["back"] : [])];
              return views.length > 1 && (
              <div className="absolute bottom-5 right-5 z-10 flex border-2 border-black shadow-hard" data-testid="view-toggle">
                {views.map((v) => (
                  <button
                    key={v}
                    data-testid={`view-${v}`}
                    onClick={() => setView(v)}
                    className={`px-4 py-2 font-mono text-xs font-bold uppercase tracking-widest transition-colors ${
                      view === v ? "bg-primary text-white" : "bg-background hover:bg-accent"
                    }`}
                  >
                    {v === "model" ? "On body" : v}
                  </button>
                ))}
              </div>
            );
            })()}
          </div>
        </div>

        <div className="px-6 sm:px-12 py-14 lg:py-20">
          <Reveal>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-primary" data-testid="product-type">
              {product.type === "hoodie" ? "Heavyweight Hoodie · 340 gsm · Unisex" : product.type === "baby-tee" ? "Fitted Baby Tee" : product.type === "womens-tee" ? "Women's Relaxed · NL3910" : "Unisex Heavyweight Tee · 6.1 oz"} — {color || product.color}
            </p>
            <h1 className="mt-4 font-display uppercase text-5xl sm:text-6xl leading-[0.85] tracking-tighter" data-testid="product-name">
              {product.name}
            </h1>
            <p className="mt-4 font-mono text-sm font-bold uppercase tracking-wider text-primary" data-testid="product-slogan">
              "{product.slogan}"
            </p>
            <p className="mt-6 font-mono text-sm leading-relaxed text-black/70" data-testid="product-description">
              {product.description.split(/ (?=Cut from|Built on|Lightweight ring-spun)/)[0]}
            </p>
            <p className="mt-8 font-display text-4xl text-primary" data-testid="product-price">${product.price.toFixed(2)}</p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-8 rounded-xl border border-black/10 bg-white p-6 sm:p-8 shadow-[6px_6px_0_0_rgba(10,10,10,0.07)]" data-testid="purchase-panel">
            <p className="mb-3 font-mono text-xs font-bold uppercase tracking-[0.2em]">Color</p>
            <div className="flex flex-wrap gap-2" data-testid="color-selector">
              {(product.colors || [product.color]).map((c) => (
                <button
                  key={c}
                  data-testid={`color-${c.toLowerCase()}`}
                  aria-pressed={color === c}
                  onClick={() => {
                    setColor(c);
                    setSearchParams({ color: c }, { replace: true });
                    track("color_select", { slug: product.slug, color: c });
                    const avail = sizesForColor(product, c);
                    if (!avail.includes(size)) setSize(avail.includes("M") ? "M" : avail[0]);
                  }}
                  className={`flex items-center gap-2 rounded-md border px-4 py-2 font-mono text-xs font-bold uppercase tracking-wider transition-colors ${
                    color === c ? "border-black bg-black text-background" : "border-black/25 bg-white hover:border-black"
                  }`}
                >
                  <span className="h-4 w-4 border border-black" style={{ background: COLOR_SWATCHES[c] || "#999" }} />
                  {c}
                </button>
              ))}
            </div>

            <div className="mt-8 mb-3 flex items-center justify-between gap-4">
              <p className="font-mono text-xs font-bold uppercase tracking-[0.2em]">Size</p>
              <SizeChart productType={product.type} sizes={product.sizes} />
            </div>
            <div className="flex flex-wrap gap-2" data-testid="size-selector">
              {sizesForColor(product, color).map((s) => (
                <button
                  key={s}
                  data-testid={`size-${s}`}
                  aria-pressed={size === s}
                  onClick={() => setSize(s)}
                  className={`h-12 w-14 rounded-md border font-mono text-sm font-bold transition-colors ${
                    size === s ? "border-black bg-black text-background" : "border-black/25 bg-white hover:border-black"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>

            <p className="mt-8 mb-3 font-mono text-xs font-bold uppercase tracking-[0.2em]">Quantity</p>
            <div className="flex items-center gap-4">
              <button
                data-testid="qty-minus"
                aria-label="Decrease quantity"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="h-12 w-12 rounded-md border border-black/25 bg-white flex items-center justify-center hover:border-black transition-colors"
              >
                <Minus size={16} />
              </button>
              <span className="font-mono text-lg font-bold w-6 text-center" data-testid="qty-value">{qty}</span>
              <button
                data-testid="qty-plus"
                aria-label="Increase quantity"
                onClick={() => setQty((q) => q + 1)}
                className="h-12 w-12 rounded-md border border-black/25 bg-white flex items-center justify-center hover:border-black transition-colors"
              >
                <Plus size={16} />
              </button>
            </div>

            <button
              data-testid="add-to-cart-button"
              onClick={() => {
                addItem(product, size, color, qty);
                track("add_to_cart", { slug: product.slug, color });
                toast.success(`Added (${color} / ${size}). Go be loud.`);
              }}
              className="mt-8 w-full rounded-lg border-2 border-black bg-primary py-5 font-display uppercase text-2xl text-white shadow-hard btn-press hover:bg-black"
            >
              Add to cart — ${(product.price * qty).toFixed(2)}
            </button>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 font-mono text-[11px] uppercase tracking-wider text-black/60" data-testid="pdp-trust-row">
              <span className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-primary" /> Secure Stripe checkout</span>
              <span className="flex items-center gap-1.5"><Truck size={14} className="text-primary" /> First item ships free $65+ · +$1.95/extra item</span>
              <span className="flex items-center gap-1.5"><RotateCcw size={14} className="text-primary" /> 30-day defect cover</span>
            </div>
            {charity && (
              <p className="mt-4 flex items-center justify-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-black/60 whitespace-nowrap" data-testid="pdp-charity-line">
                <Heart size={14} className="shrink-0 text-primary" />
                5% of annual net profits → {charity.url ? (
                  <a href={charity.url} target="_blank" rel="noreferrer" className="font-bold text-black underline underline-offset-2">{charity.name}</a>
                ) : (
                  <span className="font-bold text-black">{charity.name}</span>
                )}
              </p>
            )}
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="mt-8 border-2 border-black bg-[#111] shadow-hard" data-testid="print-closeup" hidden={!closeupOk}>
              <img
                src={imgUrl(`/api/static/products/closeups/${product.slug}.webp`)}
                alt={`${product.name} — print close-up detail`}
                loading="lazy"
                onError={() => setCloseupOk(false)}
                className="w-full object-contain"
              />
              <p className="border-t-2 border-black bg-background px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-black/60 leading-relaxed">
                Print close-up — actual print detail, straight from the production artwork. Model photos above are mockups; this is the real ink.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <Accordion type="single" collapsible className="mt-12 border-t-2 border-black">
              <AccordionItem value="fabric" className="border-b-2 border-black">
                <AccordionTrigger className="font-display uppercase text-lg hover:no-underline">The Fabric</AccordionTrigger>
                <AccordionContent className="font-mono text-sm leading-relaxed text-black/70">
                  {product.type === "hoodie"
                    ? "10 oz (340 gsm) heavyweight three-end fleece (Lane Seven LS19001): 70% cotton / 30% recycled polyester with a 100% combed-cotton face. Three-panel fleece-lined hood, kangaroo pocket for hands or pamphlets. Relaxed unisex fit with room to layer — take your usual size. Pre-shrunk, like your faith in polling."
                    : product.type === "baby-tee"
                      ? "Fitted micro-rib baby tee (Bella+Canvas 1010): 5.3 oz 1x1 rib, 52% Airlume combed ring-spun cotton / 48% polyester. Mid-length cut, crew neck. Size up if you want it less fitted."
                      : product.type === "womens-tee"
                        ? "Relaxed women's tee (Next Level NL3910 boyfriend cut): 4.3 oz 100% combed ring-spun cotton — soft without going flimsy, side-seamed in a true women's relaxed fit (Heather Grey is 90/10 cotton-poly). Take your usual size, XS through 2XL."
                        : "6.1 oz garment-dyed ring-spun cotton (Comfort Colors 1717). Relaxed unisex fit — take your usual size. Lived-in feel that softens with every wash. Thick enough to survive the wash cycle and the news cycle."}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="print" className="border-b-2 border-black">
                <AccordionTrigger className="font-display uppercase text-lg hover:no-underline">The Print</AccordionTrigger>
                <AccordionContent className="font-mono text-sm leading-relaxed text-black/70">
                  Printed direct-to-garment with water-based inks and mild resentment. Cracks beautifully with age, like every institution it mocks. Designed to be worn loudly: grocery stores, school pickups, family holidays.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="care" className="border-b-2 border-black">
                <AccordionTrigger className="font-display uppercase text-lg hover:no-underline">Wash Care</AccordionTrigger>
                <AccordionContent className="font-mono text-sm leading-relaxed text-black/70">
                  Machine wash cold, inside out, hang dry — and never iron the print. It ages like the arguments on it: proudly.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="shipping" className="border-b-2 border-black">
                <AccordionTrigger className="font-display uppercase text-lg hover:no-underline">Shipping & Returns</AccordionTrigger>
                <AccordionContent className="font-mono text-sm leading-relaxed text-black/70">
                  Printed to order: production takes 2–7 business days, then tracked US delivery typically lands in 2–5 more. Shipping is $4.95 for the first item and $1.95 per extra item; on orders $65+ the first item ships free (multi-item orders may arrive in separate tracked parcels). Misprints or defects: free replacement or refund within 30 days of delivery; wrong size? Email us within 30 days and we review it case by case — unlike certain press conferences, we actually answer.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Reveal>

          {related.length > 0 && (
            <Reveal delay={0.18}>
              <div className="mt-12 border-t-2 border-black pt-10" data-testid="related-products">
                <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-primary mb-6">
                  Same slogan, other cuts
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
                  {related.map((r) => (
                    <Link
                      key={r.id}
                      to={`/product/${r.slug}`}
                      data-testid={`related-${r.slug}`}
                      className="group block border-2 border-black bg-white shadow-hard btn-press"
                    >
                      <div className="relative aspect-[4/5] overflow-hidden border-b-2 border-black bg-[#111]">
                        <img
                          src={imgUrl(r.model_image || r.image)}
                          alt={`${r.name} — ${r.card_color || r.color}`}
                          loading="lazy"
                          className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-500 ease-out group-hover:scale-105"
                        />
                      </div>
                      <div className="p-4">
                        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                          {r.type === "hoodie" ? "Heavyweight Hoodie" : r.type === "womens-tee" ? "Women's Relaxed" : r.type === "baby-tee" ? "Fitted Baby Tee" : "Unisex Heavyweight"}
                        </p>
                        <p className="mt-1 font-display uppercase text-base leading-tight group-hover:underline decoration-primary decoration-2 underline-offset-4">
                          {r.name}
                        </p>
                        <p className="mt-1 font-mono text-xs font-bold text-primary">${r.price.toFixed(2)}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>
          )}

          <ReviewsSection slug={product.slug} />
        </div>
      </div>
    </main>
  );
}
